import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, Loader2, X } from "lucide-react";

import { ANALYTICS_QUERY_KEY } from "../../api/hooks/useSummary";
import { IMPORT_USAGE_QUERY_KEY } from "../../api/hooks/useImportUsage";
import { TRADES_QUERY_KEY } from "../../api/hooks/useTrades";
import {
  isImportJobActive,
  useImportJobs,
  type ImportJob,
} from "../../api/hooks/useImportJobs";
import { isFeatureEnabled } from "../../config/features";
import { FeedbackDialog } from "../feedback/FeedbackDialog";

// How long the green "complete" banner lingers before auto-hiding. The user can
// dismiss it sooner with the X button. Error banners do NOT auto-dismiss -- they
// persist until dismissed so the "Report this" affordance stays reachable
// (operator decision, Unit 1).
const COMPLETE_VISIBLE_MS = 6000;

type Failure = { jobId: string; filename: string; error: string | null };

type Completion = { tradesCreated: number; failures: ReadonlyArray<Failure> };

function summarizeFinished(jobs: ReadonlyArray<ImportJob>): Completion {
  let tradesCreated = 0;
  const failures: Failure[] = [];
  for (const job of jobs) {
    // STALE (reaper-interrupted) is not a clean success -- surface it as an
    // error alongside FAILED so the user knows to re-import (ADR 0034).
    if (job.status === "failed" || job.status === "stale") {
      failures.push({
        jobId: job.job_id,
        filename: job.filename,
        error: job.error_message,
      });
    } else {
      tradesCreated += job.trades_created ?? 0;
    }
  }
  return { tradesCreated, failures };
}

// Aggregate measured progress across the active jobs that report a total
// (ADR 0034). Returns null when no active job has a usable total yet, so the
// caller falls back to the indeterminate spinner (no divide-by-zero).
function aggregateProgress(
  jobs: ReadonlyArray<ImportJob>,
): { processed: number; total: number; pct: number } | null {
  let processed = 0;
  let total = 0;
  for (const job of jobs) {
    if (job.fills_total != null && job.fills_total > 0) {
      total += job.fills_total;
      processed += job.fills_processed ?? 0;
    }
  }
  if (total === 0) return null;
  const pct = Math.min(100, Math.round((processed / total) * 100));
  return { processed, total, pct };
}

// Seed the feedback dialog's message with the failing batch(es) + error text so
// the operator gets an actionable report rather than a bare category. The job id
// is included so a report can be tied back to the import_jobs row (and its
// retained source file).
function buildErrorReport(failures: ReadonlyArray<Failure>): string {
  const lines = ["My import failed. Details below.", ""];
  for (const f of failures) {
    lines.push(`File: ${f.filename} (job ${f.jobId})`);
    lines.push(`Error: ${f.error ?? "unknown"}`);
    lines.push("");
  }
  return lines.join("\n").trimEnd();
}

const BANNER_BASE =
  "flex items-center gap-3 border-b px-4 py-2 text-sm border-outline";

// Global import surface (ADR 0023 / L0-U15, ADR 0034). Full-width banner mounted
// in AppShell with the other banners, so it persists across navigation and reads
// as part of the shell's banner family. Three states: in-progress (blue, with a
// determinate fills bar once the feature flag is on), complete (green, auto-
// dismisses), and error (red, persists until dismissed with a "Report this"
// button that opens the feedback dialog pre-filled). Job state lives server-side,
// so navigating away and back re-attaches to the same progress. Replaces the
// former top-right ImportProgressPill (Unit 1).
export function ImportProgressBanner() {
  const { data } = useImportJobs();
  const queryClient = useQueryClient();

  const jobs = data?.jobs ?? [];
  const activeCount = jobs.filter(isImportJobActive).length;

  const prevActiveIdsRef = useRef<ReadonlySet<string>>(new Set());
  const dismissTimerRef = useRef<number | undefined>(undefined);
  const [completion, setCompletion] = useState<Completion | null>(null);

  useEffect(() => {
    const currentJobs = data?.jobs ?? [];
    const activeNow = currentJobs.filter(isImportJobActive);
    const prevActiveIds = prevActiveIdsRef.current;
    prevActiveIdsRef.current = new Set(activeNow.map((job) => job.job_id));

    // A fresh import started -> drop any lingering "complete"/"error" banner.
    if (activeNow.length > 0 && completion !== null) {
      setCompletion(null);
      return;
    }

    // Jobs that were running on the previous poll and have since terminated
    // are the batch that just finished.
    const justFinished = currentJobs.filter(
      (job) => prevActiveIds.has(job.job_id) && !isImportJobActive(job),
    );
    if (justFinished.length === 0) {
      return;
    }

    const summary = summarizeFinished(justFinished);
    setCompletion(summary);
    void queryClient.invalidateQueries({ queryKey: TRADES_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: ANALYTICS_QUERY_KEY });
    void queryClient.invalidateQueries({ queryKey: IMPORT_USAGE_QUERY_KEY });

    if (dismissTimerRef.current !== undefined) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = undefined;
    }
    // Only a clean success auto-dismisses; an error persists so "Report this"
    // stays reachable.
    if (summary.failures.length === 0) {
      dismissTimerRef.current = window.setTimeout(() => {
        setCompletion(null);
      }, COMPLETE_VISIBLE_MS);
    }
  }, [data, completion, queryClient]);

  useEffect(
    () => () => {
      if (dismissTimerRef.current !== undefined) {
        window.clearTimeout(dismissTimerRef.current);
      }
    },
    [],
  );

  const dismiss = () => {
    if (dismissTimerRef.current !== undefined) {
      window.clearTimeout(dismissTimerRef.current);
      dismissTimerRef.current = undefined;
    }
    setCompletion(null);
  };

  if (activeCount > 0) {
    // Measured bar (ADR 0034) is gated: with the flag off it stays the
    // indeterminate spinner. When on, render a determinate bar only if an
    // active job reports a total -- otherwise fall back to the spinner.
    const progress = isFeatureEnabled("liveImportProgressBar")
      ? aggregateProgress(jobs.filter(isImportJobActive))
      : null;
    const files = `${activeCount} file${activeCount > 1 ? "s" : ""}`;
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="import-progress-banner"
        className="border-b border-outline bg-info/10 text-on-surface"
      >
        <div className="flex items-center gap-3 px-4 py-2 text-sm">
          <Loader2 aria-hidden="true" className="h-4 w-4 shrink-0 animate-spin" />
          <span className="flex-1">
            Importing {files}
            {progress !== null ? (
              <>
                {" "}
                &mdash; <strong>{progress.pct}%</strong>
              </>
            ) : (
              <>&hellip;</>
            )}
          </span>
          <Link
            to="/imports/new"
            data-testid="import-progress-banner-link"
            className="shrink-0 font-medium text-primary underline-offset-2 hover:underline"
          >
            View import
          </Link>
        </div>
        {progress !== null ? (
          <div
            data-testid="import-progress-bar"
            aria-hidden="true"
            className="h-1 w-full overflow-hidden bg-surface-container-high"
          >
            <div
              className="h-full bg-primary transition-[width]"
              style={{ width: `${progress.pct}%` }}
            />
          </div>
        ) : null}
      </div>
    );
  }

  if (completion !== null) {
    if (completion.failures.length > 0) {
      const count = completion.failures.length;
      const firstError = completion.failures[0]?.error;
      return (
        <div
          role="alert"
          data-testid="import-error-banner"
          className={`${BANNER_BASE} bg-error/10`}
        >
          <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0" />
          <span className="flex-1">
            Import finished with <strong>{count}</strong> error
            {count > 1 ? "s" : ""}.
            {firstError ? (
              <span className="text-on-surface-variant"> {firstError}</span>
            ) : null}
          </span>
          <FeedbackDialog
            trigger={
              <button
                type="button"
                data-testid="import-error-banner-report"
                className="shrink-0 rounded-control border border-outline px-2 py-1 text-xs font-medium text-on-surface hover:bg-surface-container"
              >
                Report this
              </button>
            }
            initialCategory="bug"
            initialMessage={buildErrorReport(completion.failures)}
            initialIncludeScreenshot
          />
          <button
            type="button"
            onClick={dismiss}
            aria-label="Dismiss import notification"
            data-testid="import-error-banner-dismiss"
            className="shrink-0 rounded-control p-1 text-on-surface-variant hover:bg-surface-container"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      );
    }

    const { tradesCreated } = completion;
    return (
      <div
        role="status"
        aria-live="polite"
        data-testid="import-complete-banner"
        className={`${BANNER_BASE} bg-success/10`}
      >
        <CheckCircle2 aria-hidden="true" className="h-4 w-4 shrink-0" />
        <span className="flex-1">
          Import complete &middot; <strong>{tradesCreated}</strong> trade
          {tradesCreated === 1 ? "" : "s"}.{" "}
          <Link
            to="/trades"
            data-testid="import-complete-banner-view-trades"
            className="font-medium text-primary underline-offset-2 hover:underline"
          >
            View trades
          </Link>
        </span>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss import notification"
          data-testid="import-complete-banner-dismiss"
          className="shrink-0 rounded-control p-1 text-on-surface-variant hover:bg-surface-container"
        >
          <X aria-hidden="true" className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return null;
}
